import { z } from 'zod';

/* Einstellungen → DXL-App & Stripe (seller_get, app_key, app_install_url, app_strict, stripe_cfg_* der PHP-Middleware) */

export const DXL_APP_STANDARD = 'https://dxl-marketplace.fly.dev';

/** Geheimer Stripe-Schlüssel – wie stripe_cfg_save */
export const STRIPE_SCHLUESSEL = /^(sk|rk)_(test|live)_[A-Za-z0-9]{10,}$/;

export const stripeModus = (schluessel: string): 'live' | 'test' => (/^(sk|rk)_live_/.test(schluessel) ? 'live' : 'test');

const httpsAdresse = (meldung: string) =>
  z
    .string()
    .trim()
    .max(500, 'Höchstens 500 Zeichen')
    .refine((v) => /^https:\/\/[^\s/]+[^\s]*$/i.test(v), meldung);

export const AppRegistrierungSchema = z.object({
  url: httpsAdresse('Adresse der DXL-App fehlt (https://…).').transform((v) => v.replace(/\/+$/, '')),
});
export type AppRegistrierung = z.infer<typeof AppRegistrierungSchema>;

export const AppInstallSchema = z.object({
  url: z.union([z.literal(''), httpsAdresse('Bitte einen vollständigen Link mit https:// eintragen.')]).default(''),
});
export type AppInstall = z.infer<typeof AppInstallSchema>;

export const AppStrengSchema = z.object({ an: z.boolean() });

export const StripeSchluesselSchema = z.object({
  schluessel: z
    .string()
    .trim()
    .regex(STRIPE_SCHLUESSEL, 'Bitte den geheimen Schlüssel (sk_live_… oder sk_test_…) eintragen.'),
});
export type StripeSchluesselEingabe = z.infer<typeof StripeSchluesselSchema>;

/* ------------------------------------------------------------------ Zahlungsanbieter für Auszahlungen an Händler */

export const ZAHLUNGSANBIETER = { stripe: 'Stripe', mollie: 'Mollie', nuvei: 'Nuvei' } as const;
export type Zahlungsanbieter = keyof typeof ZAHLUNGSANBIETER;
export const ZAHLUNGSANBIETER_LISTE = Object.keys(ZAHLUNGSANBIETER) as Zahlungsanbieter[];

export const ZahlungsanbieterWahlSchema = z.object({ anbieter: z.enum(['stripe', 'mollie', 'nuvei']) });

/** Mollie Connect: Organisations-Zugriffstoken des Marktplatzes und seine OAuth-App (für die Händler) */
export const MollieZugangSchema = z.object({
  token: z.string().trim().regex(/^access_[A-Za-z0-9]{20,}$/, 'Bitte das Organisations-Zugriffstoken (access_…) eintragen.'),
  clientId: z.string().trim().regex(/^app_[A-Za-z0-9]{6,}$/, 'Die Client-ID der OAuth-App beginnt mit app_.'),
  clientSecret: z.string().trim().min(16, 'Bitte das Client-Secret der OAuth-App eintragen.').max(200),
  test: z.boolean().default(false),
});
export type MollieZugangEingabe = z.input<typeof MollieZugangSchema>;

/** Nuvei for Platforms: Zugriffstoken (von Nuvei ausgestellt), Umgebung und optional das Konto, aus dem ausgezahlt wird */
export const NuveiZugangSchema = z.object({
  token: z.string().trim().min(16, 'Bitte das Zugriffstoken von Nuvei for Platforms eintragen.').max(2000),
  umgebung: z.enum(['sandbox', 'live']),
  konto: z.string().trim().max(80).default(''),
});
export type NuveiZugangEingabe = z.input<typeof NuveiZugangSchema>;

export interface AnbieterStand {
  eingerichtet: boolean;
  modus: 'live' | 'test' | null;
  /** z. B. Anfang des Schlüssels oder Name der Organisation */
  anzeige: string;
  geprueftAm: string | null;
  /** Händler mit Konto bei diesem Anbieter */
  konten: number;
}

export interface ZahlungEinstellungen {
  anbieter: Zahlungsanbieter;
  stripe: AnbieterStand;
  mollie: AnbieterStand & { organisation: string };
  nuvei: AnbieterStand & { konto: string };
  /** Mollie: in der OAuth-App als Redirect-URL einzutragen */
  mollieRueckruf: string;
}

/** Was sich aus dem Installationslink ablesen lässt (app_install_info) */
export type AppInstallInfo =
  | { art: 'leer' }
  | { art: 'appstore' }
  | { art: 'sonstig' }
  | { art: 'custom'; shop: string; ablauf: string | null; abgelaufen: boolean };

function signaturLesen(signatur: string): { permanent_domain?: unknown; expires_at?: unknown } | null {
  try {
    const j: unknown = JSON.parse(atob(signatur.split('--')[0]!));
    return j && typeof j === 'object' ? (j as { permanent_domain?: unknown; expires_at?: unknown }) : null;
  } catch {
    return null;
  }
}

/** Benutzerdefinierte App: Shop und Ablauf stehen in der Signatur (Base64-JSON vor „--“) */
export function appInstallInfo(url: string, jetzt = Date.now()): AppInstallInfo {
  if (!url) return { art: 'leer' };
  let adresse: URL;
  try {
    adresse = new URL(url);
  } catch {
    return { art: 'sonstig' };
  }
  const signatur = adresse.searchParams.get('signature');
  if (url.includes('install_custom_app') && signatur) {
    const j = signaturLesen(signatur);
    const ab = j && typeof j.expires_at === 'number' ? j.expires_at : 0;
    return {
      art: 'custom',
      shop: j && typeof j.permanent_domain === 'string' ? j.permanent_domain : '',
      ablauf: ab ? new Date(ab * 1000).toISOString() : null,
      abgelaufen: ab > 0 && ab * 1000 < jetzt,
    };
  }
  if (/^https:\/\/apps\.shopify\.com\//i.test(url)) return { art: 'appstore' };
  return { art: 'sonstig' };
}

export interface AppShop {
  haendlerId: string;
  firma: string;
  shop: string;
  verbundenAm: string | null;
  /** Händlerschlüssel vorhanden (nur der Hash ist gespeichert) */
  schluessel: boolean;
  ohneSchluesselZuletzt: string | null;
}

export interface AppEinstellungen {
  kuerzel: string;
  name: string;
  /** Adresse dieser Middleware, wie sie der App gemeldet wird */
  middlewareUrl: string;
  registrierung: { url: string; registriertAm: string | null; fehler: string };
  schluessel: { gesetzt: boolean; erzeugtAm: string | null };
  installUrl: string;
  installInfo: AppInstallInfo;
  streng: boolean;
  shops: AppShop[];
  /** weitere verbundene Shops (Liste zeigt höchstens 200) */
  shopsWeitere: number;
  stripe: {
    eingerichtet: boolean;
    modus: 'live' | 'test' | null;
    /** Anfang des Schlüssels, z. B. „sk_live_51Ab…“ */
    praefix: string;
    geprueftAm: string | null;
    konten: number;
  };
  zahlung: ZahlungEinstellungen;
}

/** Nur direkt nach dem Erzeugen – danach ist der Schlüssel nicht mehr abrufbar */
export interface AppSchluesselNeu {
  schluessel: string;
  einstellungen: AppEinstellungen;
}
